import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Category } from '../types';

interface UseCategoriesReturn {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  getCategoryLabel: (value: string) => string;
  getCategoryColor: (value: string) => string;
}

export function useCategories(): UseCategoriesReturn {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/categories');
      setCategories(res.data);
      setError(null);
    } catch (err) {
      setError('Failed to load categories');
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const getCategoryLabel = useCallback(
    (value: string): string => {
      const found = categories.find((c) => c.value === value);
      return found ? found.label : value;
    },
    [categories]
  );

  const getCategoryColor = useCallback(
    (value: string): string => {
      const found = categories.find((c) => c.value === value);
      return found ? found.color : '#00b7ed';
    },
    [categories]
  );

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    getCategoryLabel,
    getCategoryColor,
  };
}
